// Avatar — renders the user's Graph photo via /api/users/:id/avatar with a
// graceful fallback to initials if the endpoint returns 404 or the request
// fails. Same visual size/shape as the old initials bubble so calling sites
// can drop it in without layout shift.
//
// Bot-Accounts werden hier zentral markiert (Ring in tertiary + Bot-Glyph
// statt Initialen), damit ein Bot auch in reinen Avatar-Kontexten ohne Namen
// erkennbar ist — etwa in den Assignee-Stapeln der Aufgabenlisten. Ring und
// Glyph liegen innerhalb der bestehenden Box, das Layout verschiebt sich nicht.

import { useState } from "preact/hooks";
import { Bot } from "lucide-preact";
import { initials } from "../lib/format";
import { findUser } from "../store/users";
import { botTitle, resolveBot } from "./BotBadge";

interface Props {
  /** User id; we look up the public profile via the users store. */
  userId: number;
  /** Pixel size of the circle. Default 20 (matches the old size-5 bubble). */
  size?: number;
  /** Override the displayed name (used when caller already has a Public DTO). */
  name?: string;
  /** Optional avatar URL override; otherwise derived from the user. */
  avatarUrl?: string | null;
  /**
   * Bot-Eigenschaft direkt aus dem Payload (Autor-DTOs). Nötig, weil der
   * Users-Store nur aktive Accounts kennt — ein deaktivierter Bot wäre dort
   * nicht mehr auflösbar und sein Avatar fiele auf Initialen zurück.
   */
  isBot?: boolean;
  botOwnerId?: number | null;
  className?: string;
}

/** Icon scale the app renders lucide glyphs at — nothing in between. */
const ICON_SCALE = [14, 16, 20, 24] as const;

/** Snap an arbitrary px value to the nearest allowed icon size (ties round up). */
function snapIconSize(px: number): (typeof ICON_SCALE)[number] {
  let best: (typeof ICON_SCALE)[number] = ICON_SCALE[0];
  let bestDist = Infinity;
  for (const step of ICON_SCALE) {
    const dist = Math.abs(step - px);
    if (dist < bestDist || (dist === bestDist && step > best)) {
      best = step;
      bestDist = dist;
    }
  }
  return best;
}

export function UserAvatar(props: Props): preact.JSX.Element {
  const { userId, size = 20, name, avatarUrl, className } = props;
  const user = findUser(userId);
  const displayName = name ?? user?.name ?? `User #${userId}`;
  const url = avatarUrl ?? user?.avatar_url ?? null;
  const [broken, setBroken] = useState(false);

  const bot = resolveBot({
    userId,
    isBot: props.isBot,
    ownerId: props.botOwnerId,
  });
  const isBot = bot.isBot;
  // Bots: eigene Füllfarbe plus Ring. Der Ring ist ein box-shadow, kostet also
  // keinen Platz im Layout. Farben werden ausgewählt statt angehängt — zwei
  // konkurrierende bg-Utilities hätten keine definierte Reihenfolge.
  const skin = isBot
    ? "bg-[color:var(--color-tertiary-container)] text-[color:var(--color-on-tertiary-container)] ring-1 ring-[color:var(--color-tertiary)]"
    : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface)]";
  const label = isBot
    ? `${displayName} — ${botTitle(bot.ownerId)}`
    : displayName;

  const style = { width: `${size}px`, height: `${size}px` };
  const baseClass = `inline-flex shrink-0 items-center justify-center rounded-full ${skin} font-semibold overflow-hidden ${
    className ?? ""
  }`;
  const fontSize = `${Math.max(11, Math.round(size * 0.45))}px`;

  if (url && !broken) {
    return (
      <span class={baseClass} style={style} role="img" title={label} aria-label={label}>
        <img
          src={url}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          onError={() => setBroken(true)}
          class="size-full object-cover"
        />
      </span>
    );
  }

  return (
    <span
      class={baseClass}
      style={{ ...style, fontSize }}
      role="img"
      title={label}
      aria-label={label}
    >
      {isBot ? (
        // Glyph statt Initialen: bei size=20 noch klar lesbar und eindeutiger
        // als zwei Buchstaben, die auch ein Mensch haben könnte. Die Glyphe
        // selbst bleibt auf der Icon-Skala (14/16/20/24), auch wenn der
        // Avatar-Kreis (size) andere Zwischenwerte hat.
        <Bot size={snapIconSize(size * 0.6)} aria-hidden="true" />
      ) : (
        initials(displayName)
      )}
    </span>
  );
}
